import type { FormikTouched } from "formik";
import { z } from "zod";

const accessScopes = ["read", "write"] as const;

export const accessScopeOptions = [
    { value: "read", label: "Read-only" },
    { value: "write", label: "Read & write" },
] as const;

const isChecked = (message: string) =>
    z.boolean().refine((checked) => checked, { message });

/** Checkbox, Switch and Radio rows, one value per state column. */
export const choiceFieldsPlaygroundSchema = z.object({
    checkbox: z.object({
        normal: isChecked("Confirm to continue"),
        optional: z.boolean(),
        tip: z.boolean(),
        error: isChecked("Accept the terms to continue"),
        disabled: z.boolean(),
    }),
    switch: z.object({
        normal: z.boolean(),
        optional: z.boolean(),
        tip: z.boolean(),
        error: isChecked("Turn on TLS for port 465"),
        disabled: z.boolean(),
    }),
    radio: z.object({
        normal: z.enum(accessScopes),
        optional: z.enum(accessScopes).nullable(),
        tip: z.enum(accessScopes),
        error: z
            .enum(accessScopes)
            .refine((scope): boolean => scope !== "write", {
                message: "Write access needs an admin",
            }),
        disabled: z.enum(accessScopes),
    }),
});

type ChoiceFieldsPlaygroundValues = z.infer<
    typeof choiceFieldsPlaygroundSchema
>;

export const choiceFieldsPlaygroundInitialValues: ChoiceFieldsPlaygroundValues =
    {
        checkbox: {
            normal: true,
            optional: false,
            tip: false,
            error: false,
            disabled: true,
        },
        switch: {
            normal: true,
            optional: false,
            tip: true,
            error: false,
            disabled: false,
        },
        radio: {
            normal: "read",
            optional: null,
            tip: "write",
            error: "write",
            disabled: "read",
        },
    };

/** The error column starts touched so its message shows on load. */
export const choiceFieldsPlaygroundInitialTouched: FormikTouched<ChoiceFieldsPlaygroundValues> =
    {
        checkbox: { error: true },
        switch: { error: true },
        radio: { error: true },
    };
